import { useMemo, useRef, useState } from 'react'

import {
  errorView,
  newUuid,
  resourceListTag,
  useOpenWindow,
  useWindow,
  type ErrorView,
  useQueryResult,
} from '@krp/client-runtime'
import {
  CASHIERS_REVOKE_DISABLED,
  NEW_CASHIER_DRAFT,
  OWN_CASHIERS_CAPABILITY,
  OWN_CASHIER_TAG,
  OwnCashiersScreen,
  cashierNameProblem,
  cashiersVm,
  ownCashierAccessVersion,
  ownCashierRoster,
  ownCashiersApi,
  ownChangeCashier,
  ownCreateCashier,
  ownRevokeCashierAccess,
  type CashierDraft,
  type OwnCashiersActions,
} from '@krp/resource-own-ui'
import { CASHIER_ACCESS_WINDOWS } from '@krp/resource-receipts-ui'

import { useAppDispatch } from '../../app/hooks'
import { useMyAccess } from '../platform/useMyAccess'
import { useOwnCapability } from './useOwnCapability'

/** Ключ платформы на PIN и отзыв доступа кассиров в Cashiers (отдельно от права вести состав кассиров). */
const CASHIERS_ACCESS_KEY = 'platform:cashiers:manage'

interface StepError {
  title: string
  view: ErrorView
}

interface ArchiveState {
  cashierId: string
  working: boolean
  stage: string | null
  error: StepError | null
}

/**
 * `operationId` на ПОПЫТКУ: пока тело команды то же (повтор после «ответ не получен»), идёт тот же GUID и служба вернёт прежний
 * итог, а не второго кассира. Изменилось тело — новый GUID (иначе служба ответит `operation-conflict`).
 */
function useOperationId() {
  const last = useRef<{ signature: string; id: string } | null>(null)
  return {
    of: (signature: string): string => {
      if (last.current?.signature !== signature) last.current = { signature, id: newUuid() }
      return last.current.id
    },
    done: () => {
      last.current = null
    },
  }
}

/**
 * Binding окна «Кассиры» собственной кассы (RP-OWN11). Список — в кэше запроса; в состоянии окна только намерение человека
 * (черновик имени, подтверждение архивирования). Архивирование: сначала отзыв доступа в Cashiers (архивного кассира он уже не
 * находит), затем запись в архив; если права на отзыв нет — архивирует с явным предупреждением.
 */
export function OwnCashiersBinding() {
  const window = useWindow()
  const openWindow = useOpenWindow()
  const dispatch = useAppDispatch()
  const resourceId = window.params.rid ?? ''
  const canWrite = useOwnCapability(resourceId, OWN_CASHIERS_CAPABILITY)
  const access = useMyAccess()
  const canRevoke = access.data?.api.includes(CASHIERS_ACCESS_KEY) === true
  const args = useMemo(() => (resourceId === '' ? null : { resourceId }), [resourceId])
  const roster = useQueryResult(ownCashierRoster, args)

  const [draft, setDraft] = useState<CashierDraft | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const [warning, setWarning] = useState<string | null>(null)
  const [rowError, setRowError] = useState<StepError | null>(null)
  const [archive, setArchive] = useState<ArchiveState | null>(null)
  const [create, createState] = ownCreateCashier.useMutation()
  const [change, changeState] = ownChangeCashier.useMutation()
  const command = draft?.id == null ? createState : changeState
  const operation = useOperationId()
  const archiveOperation = useOperationId()
  const revokeAttempt = useRef<{ cashierId: string; operationId: string; expectedVersion: number } | null>(null)

  const items = roster.data?.items
  const find = (cashierId: string) => items?.find((item) => item.cashierId === cashierId)
  const clearNotes = () => {
    setDone(null)
    setWarning(null)
    setRowError(null)
  }

  const runArchive = async (cashierId: string) => {
    const item = find(cashierId)
    if (item === undefined) return
    const fail = (title: string, view: ErrorView) => setArchive({ cashierId, working: false, stage: null, error: { title, view } })
    clearNotes()
    let revoked = false
    let skipped: string | null = canRevoke ? null : CASHIERS_REVOKE_DISABLED
    if (canRevoke) {
      setArchive({ cashierId, working: true, stage: 'Отзываю доступ кассира в «Доступе кассиров»…', error: null })
      let attempt = revokeAttempt.current?.cashierId === cashierId ? revokeAttempt.current : null
      if (attempt === null) {
        const version = await dispatch(ownCashierAccessVersion({ resourceId, cashierId }))
        if (version.ok) {
          attempt = { cashierId, operationId: newUuid(), expectedVersion: version.data.version }
          revokeAttempt.current = attempt
        } else if (version.error.kind === 'forbidden') {
          skipped = `Доступ кассира не отозван: ${version.error.message} (код ${version.error.code}). ${CASHIERS_REVOKE_DISABLED}`
        } else {
          fail('Доступ кассира не отозван, в архив не записан', errorView(version.error))
          return
        }
      }
      if (attempt !== null) {
        const result = await dispatch(ownRevokeCashierAccess({ resourceId, cashierId, operationId: attempt.operationId, expectedVersion: attempt.expectedVersion }))
        if (result.ok) {
          revoked = true
          revokeAttempt.current = null
        } else if (result.error.kind === 'forbidden') {
          revokeAttempt.current = null
          skipped = `Доступ кассира не отозван: ${result.error.message} (код ${result.error.code}). ${CASHIERS_REVOKE_DISABLED}`
        } else {
          // «Ответ не получен»: тот же operationId и версия — повтор вернёт прежний итог; иное — читаем версию заново.
          if (result.error.kind !== 'unknown-outcome') revokeAttempt.current = null
          fail('Доступ кассира не отозван, в архив не записан', errorView(result.error))
          return
        }
      }
    }
    setArchive({ cashierId, working: true, stage: 'Записываю кассира в архив…', error: null })
    const signature = `archive:${cashierId}:${item.revision}`
    try {
      const saved = await change({
        resourceId,
        cashierId,
        operationId: archiveOperation.of(signature),
        expectedRevision: item.revision,
        name: item.name,
        archived: true,
      }).unwrap()
      archiveOperation.done()
      setArchive(null)
      setDone(
        revoked
          ? `Кассир «${saved.name}» в архиве; его доступ отозван.`
          : `Кассир «${saved.name}» в архиве.`,
      )
      if (skipped !== null) setWarning(skipped)
    } catch (caught) {
      fail(revoked ? 'Доступ отозван, но кассир не записан в архив' : 'Кассир не записан в архив', errorView(caught))
    }
  }

  const actions: OwnCashiersActions = {
    create: () => {
      createState.reset()
      clearNotes()
      setArchive(null)
      setDraft(NEW_CASHIER_DRAFT)
    },
    edit: (cashierId) => {
      const item = items?.find((candidate) => candidate.cashierId === cashierId)
      if (item === undefined) return
      changeState.reset()
      clearNotes()
      setArchive(null)
      setDraft({ id: item.cashierId, name: item.name, revision: item.revision })
    },
    draftChanged: setDraft,
    closeForm: () => setDraft(null),
    save: () => {
      if (draft === null || cashierNameProblem(draft.name, items ?? [], draft.id) !== null) return
      const name = draft.name.trim()
      const request =
        draft.id === null
          ? create({ resourceId, operationId: operation.of(`create:${name}`), name })
          : draft.revision === null
            ? null
            : change({
                resourceId,
                cashierId: draft.id,
                operationId: operation.of(`rename:${draft.id}:${draft.revision}:${name}`),
                expectedRevision: draft.revision,
                name,
                archived: items?.find((item) => item.cashierId === draft.id)?.archived ?? false,
              })
      if (request === null) return
      void request
        .unwrap()
        .then((saved) => {
          operation.done()
          setDone(draft.id === null ? `Кассир «${saved.name}» создан.` : `Кассир «${saved.name}» сохранён.`)
          setDraft(null)
        })
        .catch(() => undefined)
    },
    askArchive: (cashierId) => {
      clearNotes()
      setDraft(null)
      setArchive({ cashierId, working: false, stage: null, error: null })
    },
    cancelArchive: () => setArchive(null),
    confirmArchive: () => {
      if (archive !== null && !archive.working) void runArchive(archive.cashierId)
    },
    restore: (cashierId) => {
      const item = items?.find((candidate) => candidate.cashierId === cashierId)
      if (item === undefined) return
      clearNotes()
      setArchive(null)
      void change({
        resourceId,
        cashierId,
        operationId: operation.of(`restore:${cashierId}:${item.revision}`),
        expectedRevision: item.revision,
        name: item.name,
        archived: false,
      })
        .unwrap()
        .then((saved) => {
          operation.done()
          setDone(`Кассир «${saved.name}» возвращён из архива. Проверьте PIN в вкладке «Кассиры» раздела «Чеки и кассиры».`)
        })
        .catch((caught: unknown) => setRowError({ title: `Кассир «${item.name}» не возвращён из архива`, view: errorView(caught) }))
    },
    reload: () => dispatch(ownCashiersApi.util.invalidateTags([resourceListTag(OWN_CASHIER_TAG, resourceId)])),
    openAccess: () => openWindow(CASHIER_ACCESS_WINDOWS.list),
  }

  if (resourceId === '') return <p role="alert">Не указана собственная касса.</p>

  const vm = cashiersVm({
    roster,
    draft,
    command,
    archive,
    canRevoke,
    revokeWarning: CASHIERS_REVOKE_DISABLED,
    rowError,
    done,
    warning,
    canWrite,
  })

  return <OwnCashiersScreen vm={vm} actions={actions} />
}
